import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  MessageSquare,
  ArrowLeft,
  RefreshCw,
  Settings,
  Database,
  Eye,
  EyeOff
} from 'lucide-react';
import { useSMSSettings, useUpdateSMSSettings } from '@/api-services/hooks/useSMSSettings';

interface SMSSettingsForm {
  id?: number;
  apiUsername: string;
  apiPassword: string;
  description: string;
  provider: string;
}

const SMSSettingsPage = () => {
  const can = usePermissionCheck();
  const [formData, setFormData] = useState<SMSSettingsForm>({
    apiUsername: '',
    apiPassword: '',
    description: '',
    provider: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  const { data: smsSettings, loading, refetch } = useSMSSettings();
  const { mutate: updateSMSSettings, loading: isSaving } = useUpdateSMSSettings({
    onSuccess: () => {
      toast.success('SMS settings saved successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save SMS settings');
    }
  });

  useEffect(() => {
    if (smsSettings) {
      setFormData({
        id: smsSettings.id,
        apiUsername: smsSettings.apiUsername || '',
        apiPassword: smsSettings.apiPassword || '',
        description: smsSettings.description || '',
        provider: smsSettings.provider || ''
      });
    }
  }, [smsSettings]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.apiUsername || !formData.description || !formData.provider) {
      toast.error('Please fill in all required fields');
      return;
    }

    updateSMSSettings({
      id: formData.id,
      apiUsername: formData.apiUsername,
      apiPassword: formData.apiPassword,
      description: formData.description,
      provider: formData.provider
    });
  };

  const handleClear = () => {
    setFormData({
      apiUsername: '',
      apiPassword: '',
      description: '',
      provider: ''
    });
    toast.info('Form cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <Settings className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Notifications</h1>
              <p className="text-muted-foreground">Configure email and SMS settings</p>
            </div>
          </div>

          {/* Back Button */}
          <Link to="/admin/dashboard" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>

          {/* SMS Settings Card */}
          <Card className="border-border/50 shadow-elegant">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-xl">
                <MessageSquare className="w-5 h-5 text-primary" />
                <span>SMS Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-6">
                {/* Section Title */}
                <div className="border-b border-border pb-3 mb-6">
                  <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                    <Database className="w-5 h-5" />
                    <span>SMS API Credentials</span>
                  </h3>
                </div>

                {/* Form Fields */}
                <div className="space-y-6">
                  {/* Provider Field */}
                  <div className="space-y-2">
                    <Label htmlFor="provider" className="flex items-center space-x-1">
                      <span>Provider</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="provider"
                      name="provider"
                      type="text"
                      placeholder="Enter provider name"
                      value={formData.provider}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>

                  {/* Description Field */}
                  <div className="space-y-2">
                    <Label htmlFor="description" className="flex items-center space-x-1">
                      <span>Description</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="description"
                      name="description"
                      type="text"
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>

                  {/* API Username Field */}
                  <div className="space-y-2">
                    <Label htmlFor="apiUsername" className="flex items-center space-x-1">
                      <span>API Username</span>
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="apiUsername"
                      name="apiUsername"
                      type="text"
                      placeholder="Enter API Username"
                      value={formData.apiUsername}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>

                  {/* API Password Field */}
                  <div className="space-y-2">
                    <Label htmlFor="apiPassword" className="flex items-center space-x-1">
                      <span>API Password</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="apiPassword"
                        name="apiPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter API Password"
                        value={formData.apiPassword}
                        onChange={handleInputChange}
                        className="bg-background pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {can('Edit Distance & Wait Settings') && (
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      className="bg-primary hover:bg-primary/90 px-6"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleClear}
                      className="px-6"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SMSSettingsPage;
